import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { useEffect, useMemo, useState } from "react";
import {
  useGetAvailableCompetitors,
  useGetMarketPriceAvailableFiltersQuery,
  useGetMarketPriceReview,
  useGetPayersWithMarketData,
} from "store/queries/marketPrices";
import { useGetAllPayersQuery } from "store/queries/payers";
import { MarketPriceSetting } from "types/marketPrice";
import {
  IMarketPriceReviewCompetitorPrice,
  IMarketPriceReviewRow,
} from "types/marketPriceAnalysis";

import {
  buildCsvContent,
  buildMarketReviewCsv,
  downloadCsvFile,
  formatMarketPriceCents,
  keepPayerIfAvailable,
} from "./helpers";

// Same page size as the comparison table.
const ROWS_PER_PAGE = 10;

interface IMarketReviewTableProps {
  providerId: string;
  setting: MarketPriceSetting;
  /** A payer carried over from another setting tab; kept only if this setting has data for it. */
  initialPayerId?: number | null;
  onPayerChange?: (payerId: number | null) => void;
}

/**
 * CLE-2939 — a setting the provider has no rates of its own in: competitor
 * prices per CPT code (average, median, min, max, and each competitor on
 * expand), with no "your rate", no CMS columns and no recommendations.
 */
export function MarketReviewTable({
  providerId,
  setting,
  initialPayerId,
  onPayerChange,
}: IMarketReviewTableProps) {
  const [selectedPayerId, setSelectedPayerId] = useState<number | null>(
    initialPayerId ?? null
  );
  // Empty means every competitor — the review endpoint reads it that way.
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [selectedServiceLines, setSelectedServiceLines] = useState<string[]>(
    []
  );
  const [first, setFirst] = useState(0);
  const [expandedRows, setExpandedRows] = useState<
    any[] | DataTableExpandedRows | undefined
  >(undefined);

  const { data: payersData, isLoading: isLoadingPayers } =
    useGetAllPayersQuery();
  const { data: payersWithMarketData, isLoading: isLoadingPayersWithData } =
    useGetPayersWithMarketData({ providerId, setting });

  // No contract-terms grouping here: the provider has no contract of its own in
  // this setting, so the payer list is just the payers with market data.
  const payerOptions = useMemo<{ label: string; value: number }[]>(() => {
    if (!payersData?.data || !payersWithMarketData) return [];
    return payersData.data
      .filter(
        (payer: any) =>
          !payer.archivedAt &&
          payer.name !== "Clear Health" &&
          payersWithMarketData.includes(payer.id)
      )
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((payer: any) => ({ label: payer.name, value: payer.id }));
  }, [payersData, payersWithMarketData]);

  const isPayerListLoaded = Boolean(payersData?.data && payersWithMarketData);
  const isSelectedPayerAvailable =
    selectedPayerId !== null &&
    payerOptions.some((option) => option.value === selectedPayerId);

  const changePayer = (payerId: number | null) => {
    setSelectedPayerId(payerId);
    setSelectedCompetitors([]);
    setSelectedCptCodes([]);
    setSelectedServiceLines([]);
    setFirst(0);
    setExpandedRows(undefined);
  };

  // A payer this setting has no data for (carried over from another tab, or
  // gone after a provider change) is cleared once the payer list has loaded.
  useEffect(() => {
    if (!isPayerListLoaded || selectedPayerId === null) return;
    const kept = keepPayerIfAvailable(
      selectedPayerId,
      payerOptions.map((option) => option.value)
    );
    if (kept === null) changePayer(null);
  }, [isPayerListLoaded, selectedPayerId, payerOptions]);

  useEffect(() => {
    onPayerChange?.(selectedPayerId);
  }, [selectedPayerId, onPayerChange]);

  // Mirrors the comparison: a different provider clears the narrowing filters.
  useEffect(() => {
    setSelectedCompetitors([]);
    setSelectedCptCodes([]);
    setSelectedServiceLines([]);
    setFirst(0);
  }, [providerId]);

  const activePayerId = isSelectedPayerAvailable ? selectedPayerId : null;

  const { data: availableCompetitors, isLoading: isLoadingCompetitors } =
    useGetAvailableCompetitors(
      { providerId, payerId: activePayerId ?? undefined, setting },
      { skip: activePayerId === null }
    );
  const {
    data: availableFilters = { cptCodes: [], serviceLines: [] },
    isLoading: isLoadingFilters,
  } = useGetMarketPriceAvailableFiltersQuery(
    {
      payerId: activePayerId ?? 0,
      providerId,
      competitorNames: selectedCompetitors,
      setting,
    },
    { skip: activePayerId === null }
  );
  const cptOptions = availableFilters.cptCodes;
  const serviceLineOptions = availableFilters.serviceLines;
  const hasServiceLines = serviceLineOptions.length > 0;

  const [
    getMarketPriceReview,
    { data: reviewData, isLoading: isLoadingReview },
  ] = useGetMarketPriceReview();

  // Selected competitors first (alphabetically), then the rest — as in the comparison.
  const competitorOptions = useMemo(() => {
    if (!availableCompetitors) return [];
    return [...availableCompetitors]
      .sort((a, b) => {
        const aSelected = selectedCompetitors.includes(a);
        const bSelected = selectedCompetitors.includes(b);
        if (aSelected === bSelected) return a.localeCompare(b);
        return aSelected ? -1 : 1;
      })
      .map((name) => ({ label: name, value: name }));
  }, [availableCompetitors, selectedCompetitors]);

  const competitorsKey = selectedCompetitors.join(",");
  const cptCodesKey = selectedCptCodes.join(",");
  const serviceLinesKey = selectedServiceLines.join(",");

  // Runs on every filter change, like the comparison — there is no submit button.
  useEffect(() => {
    if (activePayerId === null) return;
    setFirst(0);
    setExpandedRows(undefined);
    getMarketPriceReview({
      providerId,
      setting,
      payerId: activePayerId,
      ...(selectedCompetitors.length > 0
        ? { competitorNames: selectedCompetitors }
        : {}),
      ...(selectedCptCodes.length > 0 ? { cptCodes: selectedCptCodes } : {}),
      ...(selectedServiceLines.length > 0
        ? { serviceLines: selectedServiceLines }
        : {}),
    });
  }, [
    activePayerId,
    providerId,
    setting,
    competitorsKey,
    cptCodesKey,
    serviceLinesKey,
    getMarketPriceReview,
  ]);

  const reviewRows = reviewData?.rows ?? [];
  const pageRows = useMemo(
    () => reviewRows.slice(first, first + ROWS_PER_PAGE),
    [reviewRows, first]
  );

  const handleDownload = () => {
    if (!reviewData || reviewData.rows.length === 0) return;
    const { headers, rows } = buildMarketReviewCsv(reviewData);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const payerName = reviewData.payerName || "Unknown";
    downloadCsvFile(
      buildCsvContent(headers, rows),
      `market_price_review_${setting}_${payerName}_${timestamp}.csv`
    );
  };

  const rowExpansionTemplate = (row: IMarketPriceReviewRow) => (
    <div className="px-3 py-2">
      <p className="text-sm font-semibold mb-2">
        Competitor prices for {row.cptCode}
      </p>
      <DataTable
        value={row.prices}
        dataKey="competitorName"
        size="small"
        showGridlines
        style={{ maxWidth: "640px" }}
      >
        <Column field="competitorName" header="Competitor" />
        <Column
          field="rate"
          header="Rate"
          body={(price: IMarketPriceReviewCompetitorPrice) =>
            formatMarketPriceCents(price.rate)
          }
          style={{ width: "160px" }}
        />
      </DataTable>
    </div>
  );

  if (isLoadingPayers || isLoadingPayersWithData) {
    return <LoadingSpinner />;
  }

  if (payerOptions.length === 0) {
    return (
      <div className="text-center p-4 text-gray-500">
        No market price data available for any payers in this setting.
      </div>
    );
  }

  let cptPlaceholder = "All if empty";
  if (activePayerId === null) {
    cptPlaceholder = "Select a payer first";
  } else if (isLoadingFilters) {
    cptPlaceholder = "Loading…";
  }

  const hasRows = Boolean(reviewData) && reviewRows.length > 0;

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">
        Competitor prices in this setting only — there is no comparison to your
        own rates here.
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex flex-column gap-2" style={{ minWidth: "340px" }}>
          <label className="text-sm font-semibold">Select Payer:</label>
          <Dropdown
            value={activePayerId}
            options={payerOptions}
            onChange={(e) => changePayer(e.value ?? null)}
            optionLabel="label"
            optionValue="value"
            placeholder="Select a payer"
            showClear
            filter
          />
        </div>

        <div
          className="flex flex-column gap-2 flex-grow-1"
          style={{ minWidth: "320px", maxWidth: "500px" }}
        >
          <label className="text-sm font-semibold">Select Competitors:</label>
          <MultiSelect
            value={selectedCompetitors}
            options={competitorOptions}
            onChange={(e) => setSelectedCompetitors(e.value || [])}
            placeholder={
              activePayerId === null
                ? "Select a payer first"
                : "All competitors"
            }
            filter
            showClear
            className="w-full"
            panelClassName="competitor-multiselect-panel"
            showSelectAll
            emptyFilterMessage="No competitors found"
            display="comma"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} competitors selected"
            disabled={activePayerId === null || isLoadingCompetitors}
          />
        </div>

        <div
          className="flex flex-column gap-2"
          style={{ minWidth: "260px", maxWidth: "360px" }}
        >
          <label className="text-sm font-semibold">CPT Codes:</label>
          <MultiSelect
            value={selectedCptCodes}
            options={cptOptions.map((c) => ({ label: c, value: c }))}
            onChange={(e) => setSelectedCptCodes(e.value ?? [])}
            placeholder={cptPlaceholder}
            filter
            showClear
            display="comma"
            maxSelectedLabels={2}
            className="w-full"
            disabled={activePayerId === null || isLoadingFilters}
            emptyFilterMessage="No CPT codes match"
          />
        </div>

        {hasServiceLines && (
          <div
            className="flex flex-column gap-2"
            style={{ minWidth: "260px", maxWidth: "360px" }}
          >
            <label className="text-sm font-semibold">Service Lines:</label>
            <MultiSelect
              value={selectedServiceLines}
              options={serviceLineOptions.map((sl) => ({
                label: sl,
                value: sl,
              }))}
              onChange={(e) => setSelectedServiceLines(e.value ?? [])}
              placeholder="All if empty"
              filter
              showClear
              display="comma"
              maxSelectedLabels={2}
              className="w-full"
              emptyFilterMessage="No service lines match"
            />
          </div>
        )}

        {activePayerId !== null && hasRows && !isLoadingReview && (
          <div className="flex align-items-end ml-auto">
            <Button
              label="Download CSV"
              icon="pi pi-download"
              onClick={handleDownload}
              className="p-button-outlined max-h-3rem"
            />
          </div>
        )}
      </div>

      {isLoadingReview && (
        <div className="flex justify-content-center p-5">
          <LoadingSpinner />
        </div>
      )}

      {activePayerId !== null &&
        reviewData &&
        !isLoadingReview &&
        (hasRows ? (
          <>
            <DataTable
              value={pageRows}
              dataKey="cptCode"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              stripedRows
              showGridlines
              responsiveLayout="scroll"
              className="mb-4"
            >
              <Column expander style={{ width: "3rem" }} />
              <Column
                field="cptCode"
                header="CPT Code"
                style={{ width: "100px" }}
              />
              <Column
                field="description"
                header="Description"
                body={(row: IMarketPriceReviewRow) => row.description || "—"}
                style={{ minWidth: "200px" }}
              />
              <Column
                field="serviceLine"
                header="Service Line"
                body={(row: IMarketPriceReviewRow) => row.serviceLine || "—"}
                style={{ width: "140px" }}
              />
              <Column
                field="volume"
                header="Your Volume"
                body={(row: IMarketPriceReviewRow) => row.volume || 0}
                style={{ width: "100px" }}
              />
              <Column
                field="competitorCount"
                header="# Competitors"
                style={{ width: "120px" }}
              />
              <Column
                field="average"
                header="Avg"
                body={(row: IMarketPriceReviewRow) =>
                  formatMarketPriceCents(row.average)
                }
                style={{ width: "120px" }}
              />
              <Column
                field="median"
                header="Median"
                body={(row: IMarketPriceReviewRow) =>
                  formatMarketPriceCents(row.median)
                }
                style={{ width: "120px" }}
              />
              <Column
                field="min"
                header="Min"
                body={(row: IMarketPriceReviewRow) =>
                  formatMarketPriceCents(row.min)
                }
                style={{ width: "120px" }}
              />
              <Column
                field="max"
                header="Max"
                body={(row: IMarketPriceReviewRow) =>
                  formatMarketPriceCents(row.max)
                }
                style={{ width: "120px" }}
              />
            </DataTable>
            {reviewRows.length > ROWS_PER_PAGE && (
              <Paginator
                first={first}
                rows={ROWS_PER_PAGE}
                totalRecords={reviewRows.length}
                onPageChange={(event: PaginatorPageState) => {
                  setFirst(event.first);
                  setExpandedRows(undefined);
                }}
              />
            )}
          </>
        ) : (
          <div className="text-center p-4 text-gray-500">
            No market prices for this selection
          </div>
        ))}

      {activePayerId === null && !isLoadingReview && (
        <div className="text-center p-4 text-gray-500">
          Please select a payer to review market prices.
        </div>
      )}
    </>
  );
}
